import React from 'react';
import {
  Checkbox, Select, TextInput, Textarea, phone, useToast,
} from '@parsina/framework';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { gql } from '../../lib/api';
import TaskDialog from '../../components/TaskDialog';
import {
  INSURANCE_CATALOGS, INSURER_ALIASES_MAX, INSURER_ALIAS_MAX, INSURER_ID, INSURER_ID_MAX,
  INSURER_KINDS, INSURER_NAME_LOCALES, INSURER_NAME_MAX, INSURER_REGISTRY_CODES,
  INSURER_REQUIRED_NAMES, INSURER_VERIFY_PROVIDERS,
} from '../../constants/countryProfile';
import { CREATE_INSURER, UPDATE_INSURER } from './queries';
import { NOTE_MAX, NOTE_MIN } from './EditGroupDialog';
import {
  CountryCode, insurerErrorMessage, insurerNameIn, useCountryName, useValueString,
} from './shared';

/** One spelling: trimmed, inner whitespace collapsed — as the service stores it. */
const squash = (text) => String(text ?? '').split(/\s+/).filter(Boolean).join(' ');

/** The aliases box (one per line) → the list the service keeps: no empties, no repeats, first spelling wins. */
export function aliasList(text) {
  const out = [];
  for (const line of String(text ?? '').split('\n')) {
    const alias = squash(line);
    if (alias && !out.includes(alias)) out.push(alias);
  }
  return out;
}

/** The form for an insurer (an edit), or a blank one in `country` (a create). */
export function draftFrom(insurer, country = '') {
  const names = Object.fromEntries(INSURER_NAME_LOCALES.map((l) => [l, '']));
  for (const n of insurer?.names || []) if (n.locale in names) names[n.locale] = n.name || '';
  // A row written before `names` existed carries only its two columns.
  if (insurer && !Object.values(names).some(Boolean)) {
    names.fa = insurer.nameFa || '';
    names.en = insurer.nameEn || '';
  }
  return {
    id: insurer?.id || '',
    country: insurer?.country || country || '',
    kind: INSURER_KINDS.includes(insurer?.kind) ? insurer.kind : '',
    names,
    aliases: (insurer?.aliases || []).join('\n'),
    registryCode: insurer?.registryCode || '',
    verifyProvider: insurer?.verifyProvider || '',
    // Taken as sent by the service: a new insurer is active unless switched off.
    isActive: insurer ? !!insurer.isActive : true,
  };
}

/**
 * What the form sends. The registry and verify keys exist in Iran only, so a
 * country without them sends "" — a country switched away from IR never
 * carries IR's keys along.
 */
export function inputFrom(draft) {
  return {
    kind: draft.kind,
    names: INSURER_NAME_LOCALES
      .map((locale) => ({ locale, name: squash(draft.names[locale]) }))
      .filter((n) => n.name),
    aliases: aliasList(draft.aliases),
    registryCode: INSURER_REGISTRY_CODES[draft.country] ? draft.registryCode : '',
    verifyProvider: INSURER_VERIFY_PROVIDERS[draft.country] ? draft.verifyProvider : '',
    isActive: !!draft.isActive,
  };
}

/** What still keeps the form from being sent, by field — the service checks every rule again. */
export function problemsOf(draft, creating) {
  const p = {};
  if (creating) {
    if (!draft.country) p.country = 'pickCountry';
    const id = draft.id.trim();
    if (!id) p.id = 'idNeeded';
    else if (!INSURER_ID.test(id)) p.id = 'idInvalid';
  }
  if (!INSURER_KINDS.includes(draft.kind)) p.kind = 'pickKind';
  if (!INSURER_REQUIRED_NAMES.some((l) => squash(draft.names[l]))) p.names = 'namesNeeded';
  const aliases = aliasList(draft.aliases);
  if (aliases.length > INSURER_ALIASES_MAX) p.aliases = 'tooManyAliases';
  else if (aliases.some((a) => a.length > INSURER_ALIAS_MAX)) p.aliases = 'aliasTooLong';
  return p;
}

const PROBLEM_ORDER = ['country', 'id', 'kind', 'names', 'aliases'];

/**
 * «افزودن بیمه‌گر» / «ویرایش» — one insurer of accounting's platform-owned
 * catalogue (PM-0246, insureradmin.InsurerAdminService), with a required change
 * note like every write on «کشورها».
 *
 * ── WHAT NEVER CHANGES ──────────────────────────────────────────────────────
 * The id and the country are chosen once: contracts, policies, service
 * configurations, letters and claims point at the id, and a clinic is offered
 * its own country's rows. On an edit they are shown, never editable; the form
 * says why. A self-pay row («آزاد») never reaches this dialog.
 *
 * ── IRAN-ONLY KEYS ──────────────────────────────────────────────────────────
 * The e-prescription registry key and the policy verifier exist for Iran's
 * insurers only, from the contract's closed lists; for any other country the
 * two fields are not offered and "" is sent.
 *
 * ── THE VERSION ─────────────────────────────────────────────────────────────
 * An edit sends back the row's `updatedAt` exactly as it was read (an opaque
 * token); a row that moved since is CONFLICT, and the form keeps what was typed.
 * null = no check — what the list gives while accounting's ListInsurers does
 * not carry the version.
 */
export default function InsurerDialog({
  open, onOpenChange, insurer = null, defaultCountry = '', catalogs = [], onSaved,
}) {
  const { t, locale, formatNumber } = useTranslation();
  const toast = useToast();
  const countryName = useCountryName();
  const valueString = useValueString();
  const creating = !insurer;

  const [draft, setDraft] = React.useState(() => draftFrom(insurer, defaultCountry));
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  // A fresh form every time it opens, from the row as the list holds it NOW.
  React.useEffect(() => {
    if (!open) return;
    setDraft(draftFrom(insurer, defaultCountry));
    setNote('');
    setError(null);
  }, [open, insurer, defaultCountry]);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setName = (l, value) => setDraft((d) => ({ ...d, names: { ...d.names, [l]: value } }));

  // The catalogues there is data for first, then every other country of the
  // kit's catalogue — each by its name in the panel's language.
  const catalogKey = [...new Set([...INSURANCE_CATALOGS, ...catalogs, ...(defaultCountry ? [defaultCountry] : [])])].join(',');
  const countries = React.useMemo(() => {
    const byName = (a, b) => countryName(a).localeCompare(countryName(b), locale);
    const known = catalogKey.split(',').filter(Boolean).sort(byName);
    const rest = (phone.COUNTRIES || []).map((r) => r.iso2).filter((c) => c && !known.includes(c)).sort(byName);
    return [...known, ...rest];
  }, [catalogKey, countryName, locale]);

  const registryCodes = INSURER_REGISTRY_CODES[draft.country] || null;
  const verifyProviders = INSURER_VERIFY_PROVIDERS[draft.country] || null;
  const problems = problemsOf(draft, creating);
  const input = inputFrom(draft);
  const changed = creating || JSON.stringify(input) !== JSON.stringify(inputFrom(draftFrom(insurer)));
  const trimmed = note.trim();
  const noteOk = trimmed.length >= NOTE_MIN && trimmed.length <= NOTE_MAX;
  const valid = Object.keys(problems).length === 0;
  const dirty = creating
    ? JSON.stringify(draft) !== JSON.stringify(draftFrom(null, defaultCountry)) || !!note
    : changed || !!note;

  const maxOf = (problem) => formatNumber(problem === 'tooManyAliases' ? INSURER_ALIASES_MAX : INSURER_ALIAS_MAX);
  const problemText = (problem) => t(`countries.insurers.editor.problem.${problem}`, { max: maxOf(problem) });
  const first = PROBLEM_ORDER.map((k) => problems[k]).find(Boolean);
  const footerNote = first
    ? problemText(first)
    : (!changed ? t('countries.edit.noChanges') : (!noteOk ? t('countries.edit.noteNeeded') : undefined));

  const name = insurerNameIn(insurer, locale);

  /** The saved row's name, for the toast — the panel's language first, like the list. */
  const savedName = (saved) => insurerNameIn(saved, locale)
    || insurerNameIn({ names: input.names, nameFa: '', nameEn: '' }, locale) || draft.id;

  const submit = async () => {
    if (pending || !valid || !changed || !noteOk) return;
    setPending(true);
    setError(null);
    try {
      let saved;
      if (creating) {
        const res = await gql(CREATE_INSURER, {
          input: { id: draft.id.trim(), country: draft.country, ...input },
          note: trimmed,
        });
        saved = res?.createInsurer;
      } else {
        const res = await gql(UPDATE_INSURER, {
          id: insurer.id,
          input,
          note: trimmed,
          expectedUpdatedAt: insurer.updatedAt ?? null,
        });
        saved = res?.updateInsurer;
      }
      onOpenChange(false);
      toast.success(creating
        ? t('countries.insurers.editor.created', { name: savedName(saved), country: countryName(draft.country) })
        : t('countries.insurers.editor.saved', { name: savedName(saved) }));
      onSaved?.(saved || null);
    } catch (e) {
      setError(insurerErrorMessage(e, t, { creating }));
    } finally {
      setPending(false);
    }
  };

  /** A key select: «هیچ» first, then the closed list, then whatever the row holds outside it. */
  const keyOptions = (list, current) => {
    const known = [...list, ...(current && !list.includes(current) ? [current] : [])];
    return [
      { value: '', label: t('countries.state.none') },
      ...known.map((k) => ({
        value: k,
        label: list.includes(k) ? `${t(`countries.insurers.registryName.${k}`)} (${k})` : k,
      })),
    ];
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={creating ? t('countries.insurers.editor.createTitle') : t('countries.insurers.editor.editTitle', { name })}
      error={error}
      pending={pending}
      dirty={dirty}
      size="lg"
      // A long form: the header and the submit stay put while the fields scroll.
      layout="frame"
      submitLabel={creating ? t('countries.insurers.editor.submitCreate') : t('countries.edit.submit')}
      submitDisabled={!valid || !changed || !noteOk}
      onSubmit={submit}
      footerNote={footerNote}
      data-testid="insurer-editor"
    >
      {creating ? (
        <>
          <p className="m-0 text-sm leading-6 text-muted-foreground">{t('countries.insurers.editor.body')}</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div data-testid="insurer-editor-country">
              <Select
                label={t('countries.insurers.editor.country')}
                required
                value={draft.country}
                onChange={(e) => set('country', e.target?.value ?? e)}
                options={countries.map((code) => ({ value: code, label: `${countryName(code)} (${code})` }))}
                placeholder={t('countries.insurers.editor.countryPick')}
                disabled={pending}
              />
            </div>
            <div data-testid="insurer-editor-id">
              <TextInput
                label={t('countries.insurers.editor.id')}
                required
                value={draft.id}
                onChange={(e) => set('id', e.target?.value ?? e)}
                dir="ltr"
                autoComplete="off"
                spellCheck={false}
                maxLength={INSURER_ID_MAX}
                placeholder={t('countries.insurers.editor.idExample')}
                helperText={problems.id === 'idInvalid' ? undefined : t('countries.insurers.editor.idRule')}
                error={problems.id === 'idInvalid' ? t('countries.insurers.editor.idInvalid') : undefined}
                disabled={pending}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-1.5" data-testid="insurer-editor-fixed">
          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">{t('countries.insurers.editor.id')}</dt>
            <dd className="m-0">
              <code {...LTR_TEXT} className="font-mono text-xs" data-testid="insurer-editor-fixed-id">{insurer.id}</code>
            </dd>
            <dt className="text-muted-foreground">{t('countries.insurers.editor.country')}</dt>
            <dd className="m-0 flex items-center gap-2" data-testid="insurer-editor-fixed-country">
              {countryName(insurer.country)}
              <CountryCode code={insurer.country} />
            </dd>
          </dl>
          <p className="m-0 text-xs leading-5 text-muted-foreground">{t('countries.insurers.editor.fixed')}</p>
        </div>
      )}

      {/* Native radios, not the pill switch: a new insurer has no kind until
          one is chosen, and a pill group with nothing selected has no tab stop. */}
      <fieldset className="flex min-w-0 flex-col gap-2" disabled={pending} data-testid="insurer-editor-kind">
        <legend className="mb-1.5 text-[11.5px] font-semibold text-muted-foreground">{t('countries.insurers.editor.kind')}</legend>
        <div role="radiogroup" aria-label={t('countries.insurers.editor.kind')} className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {INSURER_KINDS.map((k) => (
            <label
              key={k}
              className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border px-3 py-2.5 text-sm hover:bg-muted"
              data-state={draft.kind === k ? 'checked' : 'unchecked'}
              data-testid={`insurer-editor-kind-${k}`}
            >
              <input
                type="radio"
                name="insurer-editor-kind"
                value={k}
                checked={draft.kind === k}
                onChange={() => set('kind', k)}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-semibold text-foreground">{t(`countries.insurers.kind.${k}`)}</span>
                <span className="text-xs leading-5 text-muted-foreground">{t(`countries.insurers.editor.kindHint.${k}`)}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex min-w-0 flex-col gap-2" disabled={pending} data-testid="insurer-editor-names">
        <legend className="mb-1.5 text-[11.5px] font-semibold text-muted-foreground">{t('countries.insurers.editor.names')}</legend>
        <p className="m-0 text-[11.5px] leading-5 text-muted-foreground">{t('countries.insurers.editor.namesRule')}</p>
        <div className="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-2">
          {INSURER_NAME_LOCALES.map((l) => (
            <div key={l} data-testid={`insurer-editor-name-${l}`}>
              <TextInput
                label={valueString({ type: 'language' }, l)}
                value={draft.names[l]}
                onChange={(e) => setName(l, e.target?.value ?? e)}
                // User content: its own direction, whatever the panel's (DESKTOP_WEB §13).
                dir="auto"
                lang={l}
                maxLength={INSURER_NAME_MAX}
                disabled={pending}
              />
            </div>
          ))}
        </div>
      </fieldset>

      <div data-testid="insurer-editor-aliases">
        <Textarea
          label={t('countries.insurers.editor.aliases')}
          value={draft.aliases}
          onChange={(e) => set('aliases', e.target?.value ?? e)}
          helperText={problems.aliases ? undefined : t('countries.insurers.editor.aliasesHelper')}
          error={problems.aliases ? problemText(problems.aliases) : undefined}
          rows={3}
          dir="auto"
          disabled={pending}
        />
      </div>

      {registryCodes || verifyProviders ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="insurer-editor-keys">
          {registryCodes ? (
            <div data-testid="insurer-editor-registry">
              <Select
                label={t('countries.insurers.columns.registry')}
                value={draft.registryCode}
                onChange={(e) => set('registryCode', e.target?.value ?? e)}
                options={keyOptions(registryCodes, draft.registryCode)}
                placeholder={null}
                helperText={t('countries.insurers.editor.registryHint')}
                disabled={pending}
              />
            </div>
          ) : null}
          {verifyProviders ? (
            <div data-testid="insurer-editor-verify">
              <Select
                label={t('countries.insurers.columns.verify')}
                value={draft.verifyProvider}
                onChange={(e) => set('verifyProvider', e.target?.value ?? e)}
                options={keyOptions(verifyProviders, draft.verifyProvider)}
                placeholder={null}
                helperText={t('countries.insurers.editor.verifyHint')}
                disabled={pending}
              />
            </div>
          ) : null}
        </div>
      ) : draft.country ? (
        <p className="m-0 text-xs leading-5 text-muted-foreground" data-testid="insurer-editor-no-keys">
          {t('countries.insurers.editor.noRegistry', { country: countryName(draft.country) })}
        </p>
      ) : null}

      <Checkbox
        checked={draft.isActive}
        onChange={(v) => set('isActive', !!v)}
        label={t('countries.insurers.editor.active')}
        disabled={pending}
        data-testid="insurer-editor-active"
      />

      <div data-testid="insurer-editor-note">
        <Textarea
          label={t('countries.edit.note')}
          required
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          placeholder={t('countries.insurers.editor.notePlaceholder')}
          helperText={t('countries.edit.noteHelper')}
          maxLength={NOTE_MAX}
          showCount
          rows={2}
          disabled={pending}
        />
      </div>
    </TaskDialog>
  );
}
